import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Menu, X, Home, Phone, Smartphone, Wifi, ArrowRightLeft, Mail,
  Star, Gift, Percent, Info, HelpCircle, BookOpen, Shield, Globe, Link as LinkIcon, FileText,
} from "lucide-react";
import { useContent } from "@/context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CUSTOM_ICONS = {
  star: Star, gift: Gift, percent: Percent, info: Info,
  help: HelpCircle, book: BookOpen, shield: Shield, globe: Globe,
  link: LinkIcon, file: FileText,
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { brand } = useContent();
  const logoSrc = brand?.logo_url ? `${BACKEND_URL}${brand.logo_url}` : "/logo.jpg";
  const menu = brand?.menu || {};
  const labels = menu.labels || {};

  const builtIn = [
    { to: "/", label: labels.home || "Start", icon: Home, show: menu.show_home !== false },
    { to: "/abonamenty", label: labels.plans || "Abonamenty", icon: Phone, show: menu.show_plans !== false },
    { to: "/internet-mobilny", label: labels.internet || "Internet mobilny", icon: Wifi, show: menu.show_internet !== false },
    { to: "/zamow-sim", label: labels.order_sim || "Zamów SIM", icon: Smartphone, show: menu.show_order_sim !== false },
    { to: "/przenies-numer", label: labels.port_number || "Przenieś numer", icon: ArrowRightLeft, show: menu.show_port_number !== false },
    { to: "/kontakt", label: labels.contact || "Kontakt", icon: Mail, show: !!menu.show_contact },
  ].filter((l) => l.show);

  const customPages = (menu.custom_pages || [])
    .filter((p) => p && p.label && p.url)
    .map((p, i) => ({
      to: p.url,
      label: p.label,
      icon: CUSTOM_ICONS[p.icon] || Star,
      external: /^https?:\/\//i.test(p.url),
      key: `custom-${i}`,
    }));

  const links = [...builtIn, ...customPages];
  const ctaLabel = labels.cta || "Zamów teraz";
  const ctaEnabled = menu.cta_enabled !== false;
  const ctaUrl = menu.cta_url || "/zamow-sim";
  const ctaIsExternal = /^https?:\/\//i.test(ctaUrl);

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[rgba(21,41,107,0.85)] border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:justify-normal">
        {/* Logo */}
        <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2 flex-shrink-0 lg:justify-self-start">
          <img
            src={logoSrc}
            alt="Rmobile"
            data-testid="navbar-logo"
            onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/logo.jpg"; } }}
            onLoad={(e) => { if (e.currentTarget.naturalWidth < 20 && !e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/logo.jpg"; } }}
            className="w-[75px] h-[75px] object-cover"
          />
        </Link>

        {/* Nav pills — wyśrodkowane w całym navbarze (grid col środkowa) */}
        <div className="hidden lg:flex justify-center">
          <nav className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full p-1.5">
            {links.map((l) => {
              const testid = `nav-${(l.key || l.to.replace(/^\//, "") || "home").replace(/[^a-z0-9-]/gi, "-")}`;
              const cls = "group relative flex items-center gap-2 text-[15px] font-semibold px-4 py-2 rounded-full transition-all duration-200 text-white/85 hover:text-[#dc2626] hover:bg-white/10";
              if (l.external) {
                return (
                  <a key={testid} href={l.to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
                    <l.icon size={16} className="opacity-80" />
                    <span>{l.label}</span>
                  </a>
                );
              }
              return (
                <NavLink
                  key={testid}
                  to={l.to}
                  data-testid={testid}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `${cls} ${isActive ? "!bg-white !text-[#15296b] shadow-md" : ""}`
                  }
                >
                  <l.icon size={16} className="opacity-80" />
                  <span>{l.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* CTA */}
        {ctaEnabled && (
          <div className="hidden lg:block flex-shrink-0 lg:justify-self-end">
            {ctaIsExternal ? (
              <a
                href={ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-cta-order"
                className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold px-5 py-2.5 rounded-full transition-all shadow-lg shadow-[#dc2626]/30 hover:shadow-[#dc2626]/50 hover:-translate-y-0.5"
              >
                <Smartphone size={16} />
                {ctaLabel}
              </a>
            ) : (
              <Link
                to={ctaUrl}
                data-testid="nav-cta-order"
                className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold px-5 py-2.5 rounded-full transition-all shadow-lg shadow-[#dc2626]/30 hover:shadow-[#dc2626]/50 hover:-translate-y-0.5"
              >
                <Smartphone size={16} />
                {ctaLabel}
              </Link>
            )}
          </div>
        )}
        {/* Placeholder gdy CTA ukryty — zajmuje prawą kolumnę grida, żeby nav pozostał wyśrodkowany */}
        {!ctaEnabled && <div className="hidden lg:block" aria-hidden="true" />}

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#15296b]">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => {
              const testid = `mnav-${(l.key || l.to.replace(/^\//, "") || "home").replace(/[^a-z0-9-]/gi, "-")}`;
              const isActive = !l.external && loc.pathname === l.to;
              const cls = `flex items-center gap-3 px-3 py-3 rounded-lg transition ${isActive ? "bg-white text-[#15296b] font-bold" : "text-white/90 hover:bg-white/10"}`;
              if (l.external) {
                return (
                  <a key={testid} href={l.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={testid} className={cls}>
                    <l.icon size={18} />
                    {l.label}
                  </a>
                );
              }
              return (
                <Link key={testid} to={l.to} onClick={() => setOpen(false)} data-testid={testid} className={cls}>
                  <l.icon size={18} />
                  {l.label}
                </Link>
              );
            })}
            {ctaEnabled && (
              ctaIsExternal ? (
                <a
                  href={ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="mnav-cta-order"
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold py-3 rounded-full transition"
                >
                  <Smartphone size={16} />
                  {ctaLabel}
                </a>
              ) : (
                <Link
                  to={ctaUrl}
                  onClick={() => setOpen(false)}
                  data-testid="mnav-cta-order"
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold py-3 rounded-full transition"
                >
                  <Smartphone size={16} />
                  {ctaLabel}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
